import { Box, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { usePagesTranslation } from "../../../../pages/locales";
import {
    getSlidingStoryTitle,
    pickSlidingCardImageUrl,
    pickSlidingStoryIcon200Url,
} from "../../../../pages/browse-sliding/slidingBrowseUtils";
import SlidingPlayMain from "../engine/SlidingPlayMain";
import { resolveSlidingBoardPreview } from "../lib/api";
import WirePlayControls, {
    WirePlayProgressInfo,
    WirePlaySummary,
    gridSizeAvailable,
    levelAvailable,
    resolveSlidingTemplate,
} from "./WirePlayControls";
import {
    pickPlayGridDefaults,
    saveAuthenticatedPlayGridPreferences,
} from "../lib/playGridPreferences";
import LibraryCardConfigPills from "./LibraryCardConfigPills";
import LibraryPlayIconButton from "./LibraryPlayIconButton";
import PuzzlePublishedAtLine from "./PuzzlePublishedAtLine";
import HomeLightBlueMist from "./HomeLightBlueMist";
import { captionSx } from "../styles/mainStyles";
import { slidingElevatedCardSx, slidingPreviewGridProps } from "../lib/appearance";
import { CHALLENGE_GRID, DIFFICULTIES, DIFFICULTY_LABELS, QUICK_PLAY_GRID } from "../routes";
import {
    isDifficultyLocked,
    isGridSizeLocked,
} from "../lib/subscriptionManager";
import { useSubscription } from "../lib/SubscriptionContext";
import { useSlidingAppearance } from "../lib/AppearanceContext";

const systemFont =
    '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif';

const browseCardBodySx = {
    px: 1.25,
    py: 1.25,
};

const GRID_OPTIONS = [
    { size: QUICK_PLAY_GRID, label: "4×4" },
    { size: CHALLENGE_GRID, label: "6×6" },
];

function defaultGridSize(templates) {
    if (gridSizeAvailable(templates, QUICK_PLAY_GRID)) return QUICK_PLAY_GRID;
    if (gridSizeAvailable(templates, CHALLENGE_GRID)) return CHALLENGE_GRID;
    return QUICK_PLAY_GRID;
}

function defaultDifficulty(templates, gridSize) {
    if (levelAvailable(templates, gridSize, "easy")) return "easy";
    if (levelAvailable(templates, gridSize, "medium")) return "medium";
    if (levelAvailable(templates, gridSize, "hard")) return "hard";
    return "easy";
}

export default function LibraryCard({
    group,
    playing,
    onPlay,
    initialGridSize = null,
    initialDifficulty = null,
    hidePlayButton = false,
    progressSimple = false,
    modeLine = null,
    durationCaption = null,
    thumbnailSize = null,
    freeTier = false,
    savedGridPrefs = null,
    /** `browse` = Screen 5 library cards; `wireframe` = legacy my-puzzles layout */
    variant = "wireframe",
}) {
    const { t } = usePagesTranslation();
    const { isSubscribed } = useSubscription();
    const { tokens, isDark } = useSlidingAppearance();
    const browseCardSx = {
        position: "relative",
        width: "100%",
        height: "100%",
        mb: 0,
        borderRadius: "14px",
        overflow: "hidden",
        isolation: "isolate",
        ...slidingElevatedCardSx(tokens),
        bgcolor: isDark ? "rgba(21, 37, 63, 0.82)" : "rgba(255, 255, 255, 0.78)",
        background: isDark ? "rgba(21, 37, 63, 0.82)" : "rgba(255, 255, 255, 0.78)",
        border: "none",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
    };
    const story = group?.story;
    const storyId = group?.storyId ?? story?.id;
    const title = getSlidingStoryTitle(story) || storyId || "Puzzle";
    const use200 = thumbnailSize === 200;
    const iconUrl = use200
        ? pickSlidingStoryIcon200Url(story) || pickSlidingCardImageUrl(story)
        : pickSlidingCardImageUrl(story);
    const templates = Array.isArray(group?.templates) ? group.templates : [];
    const primaryTemplate = templates[0];
    const templateId = primaryTemplate?.id ?? "";
    const templateGridSize = primaryTemplate?.gridSize ?? "";
    const templateDifficultyLevel = primaryTemplate?.difficultyLevel ?? "";

    const [gridSize, setGridSize] = useState(() => {
        const picked = pickPlayGridDefaults({
            templates,
            savedPrefs: savedGridPrefs,
            freeTier,
            initialGridSize,
            initialDifficulty,
            progressSimple,
        });
        return picked.gridSize;
    });
    const [difficulty, setDifficulty] = useState(() => {
        const picked = pickPlayGridDefaults({
            templates,
            savedPrefs: savedGridPrefs,
            freeTier,
            initialGridSize,
            initialDifficulty,
            progressSimple,
        });
        return picked.difficulty;
    });

    useEffect(() => {
        const picked = pickPlayGridDefaults({
            templates,
            savedPrefs: savedGridPrefs,
            freeTier,
            initialGridSize,
            initialDifficulty,
            progressSimple,
        });
        setGridSize((prev) => (prev === picked.gridSize ? prev : picked.gridSize));
        setDifficulty((prev) => (prev === picked.difficulty ? prev : picked.difficulty));
    }, [
        storyId,
        templateId,
        templateGridSize,
        templateDifficultyLevel,
        initialGridSize,
        initialDifficulty,
        progressSimple,
        freeTier,
        savedGridPrefs,
    ]);

    const handleGridChange = (size) => {
        const nextDifficulty = levelAvailable(templates, size, difficulty)
            ? difficulty
            : defaultDifficulty(templates, size);
        setGridSize(size);
        setDifficulty(nextDifficulty);
        void saveAuthenticatedPlayGridPreferences({
            gridSize: size,
            difficulty: nextDifficulty,
        });
    };

    const handleDifficultyChange = (d) => {
        setDifficulty(d);
        void saveAuthenticatedPlayGridPreferences({ gridSize, difficulty: d });
    };

    const handlePlay = () => {
        void saveAuthenticatedPlayGridPreferences({ gridSize, difficulty });
        if (progressSimple) {
            const tpl = templates[0] ?? resolveSlidingTemplate(templates, initialGridSize, initialDifficulty);
            if (!tpl?.id && !group?.templates?.[0]?.id) return;
            onPlay({
                slidingId: tpl?.id ?? templates[0]?.id,
                gridSize: initialGridSize ?? gridSize,
                difficulty: initialDifficulty ?? difficulty,
            });
            return;
        }
        const tpl = resolveSlidingTemplate(templates, gridSize, difficulty);
        if (!tpl?.id) return;
        onPlay({
            slidingId: tpl.id,
            gridSize,
            difficulty,
        });
    };

    const canPlay = levelAvailable(templates, gridSize, difficulty);

    const previewGridSize =
        progressSimple && initialGridSize != null && Number.isFinite(Number(initialGridSize))
            ? Number(initialGridSize)
            : gridSize;
    const previewDifficulty =
        progressSimple && initialDifficulty && DIFFICULTIES.includes(initialDifficulty)
            ? initialDifficulty
            : difficulty;

    const boardPreview = useMemo(
        () => resolveSlidingBoardPreview(group, previewGridSize, previewDifficulty),
        [group, previewGridSize, previewDifficulty],
    );

    const gridPillOptions = useMemo(
        () =>
            GRID_OPTIONS.map((opt) => {
                const available = gridSizeAvailable(templates, opt.size);
                const locked = isGridSizeLocked(opt.size, isSubscribed);
                return {
                    ...opt,
                    locked,
                    disabled: !available,
                };
            }),
        [templates, isSubscribed]
    );

    const levelPillOptions = useMemo(
        () =>
            DIFFICULTIES.map((d) => {
                const available = levelAvailable(templates, gridSize, d);
                const locked = isDifficultyLocked(gridSize, d, isSubscribed);
                return {
                    value: d,
                    label: DIFFICULTY_LABELS[d],
                    locked,
                    disabled: !available,
                };
            }),
        [templates, gridSize, isSubscribed]
    );

    if (variant === "browse" && !progressSimple && !hidePlayButton) {
        return (
            <Box component="article" sx={browseCardSx}>
                <HomeLightBlueMist isDark={isDark} />
                <Box sx={{ position: "relative", zIndex: 1 }}>
                    <Box sx={{ px: 1.25, pt: 1.25, pb: 0.75 }}>
                        <Typography
                            component="h2"
                            sx={{
                                fontFamily: systemFont,
                                fontSize: "0.875rem",
                                fontWeight: 700,
                                lineHeight: 1.3,
                                color: tokens.text,
                                wordBreak: "break-word",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                            }}
                        >
                            {title}
                        </Typography>
                        <PuzzlePublishedAtLine group={group} sx={{ mt: 0.35, mb: 0, textAlign: "left" }} />
                    </Box>

                    {boardPreview ? (
                        <Box
                            sx={{
                                width: "100%",
                                aspectRatio: "1 / 1",
                                overflow: "hidden",
                                pointerEvents: "none",
                                bgcolor: tokens.imagePlaceholderBg,
                            }}
                        >
                            <SlidingPlayMain
                                key={`${storyId}-${previewGridSize}-${previewDifficulty}-${boardPreview.startingLayout.join("-")}`}
                                imageUrl={boardPreview.imageUrl}
                                gridSize={boardPreview.gridSize}
                                startingLayout={boardPreview.startingLayout}
                                hideCompletedCaption
                                {...slidingPreviewGridProps(tokens)}
                            />
                        </Box>
                    ) : iconUrl ? (
                        <Box
                            sx={{
                                width: "100%",
                                aspectRatio: "1 / 1",
                                overflow: "hidden",
                                bgcolor: tokens.imagePlaceholderBg,
                            }}
                        >
                            <Box
                                component="img"
                                src={iconUrl}
                                alt=""
                                sx={{
                                    display: "block",
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                }}
                            />
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                width: "100%",
                                aspectRatio: "1 / 1",
                                bgcolor: tokens.imagePlaceholderBg,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography sx={{ ...captionSx, color: tokens.textMuted, m: 0 }}>No image</Typography>
                        </Box>
                    )}

                    <Box sx={browseCardBodySx}>
                        <LibraryCardConfigPills
                            gridOptions={gridPillOptions}
                            levelOptions={levelPillOptions}
                            gridSize={gridSize}
                            difficulty={difficulty}
                            onGridChange={handleGridChange}
                            onDifficultyChange={handleDifficultyChange}
                            disabled={playing}
                        />

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                pt: 0.25,
                            }}
                        >
                            <LibraryPlayIconButton
                                onClick={handlePlay}
                                disabled={!canPlay}
                                loading={playing}
                                ariaLabel={t("native.home.solvePuzzle", {
                                    defaultValue: "Solve Puzzle",
                                })}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    }

    const myPuzzlesPreviewSx = {
        width: "100%",
        maxWidth: { xs: 280, sm: 360, md: 480, lg: 560 },
        aspectRatio: "1 / 1",
        mx: "auto",
        mb: 1.5,
        border: `1px solid ${tokens.divider}`,
        bgcolor: tokens.imagePlaceholderBg,
        overflow: "hidden",
        display: "block",
        flexShrink: 0,
    };

    const imageRowSx = use200
        ? myPuzzlesPreviewSx
        : {
              width: "100%",
              maxWidth: { xs: "100%", md: 480, lg: 560 },
              aspectRatio: "1",
              mx: "auto",
              mb: 1.5,
              border: `1px solid ${tokens.divider}`,
              bgcolor: tokens.imagePlaceholderBg,
              overflow: "hidden",
              display: "block",
          };

    return (
        <Box
            sx={{
                mb: 1.5,
                py: 1.5,
                px: 2,
                textAlign: "left",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                borderRadius: "16px",
                ...slidingElevatedCardSx(tokens),
            }}
        >
            <Typography
                sx={{
                    fontSize: "0.95rem",
                    fontWeight: 700,
                    mb: durationCaption ? 0.25 : 1,
                    lineHeight: 1.35,
                    color: tokens.text,
                }}
            >
                {title}
            </Typography>

            {durationCaption ? (
                <Typography sx={{ ...captionSx, color: tokens.textMuted, mb: 1 }}>
                    {durationCaption}
                </Typography>
            ) : null}

            {boardPreview ? (
                <Box sx={myPuzzlesPreviewSx}>
                    <SlidingPlayMain
                        key={`${storyId}-${previewGridSize}-${previewDifficulty}-${boardPreview.startingLayout.join("-")}`}
                        imageUrl={boardPreview.imageUrl}
                        gridSize={boardPreview.gridSize}
                        startingLayout={boardPreview.startingLayout}
                        hideCompletedCaption
                    />
                </Box>
            ) : iconUrl ? (
                <Box
                    component="img"
                    src={iconUrl}
                    alt=""
                    sx={{
                        ...imageRowSx,
                        objectFit: use200 ? "contain" : "cover",
                    }}
                />
            ) : (
                <Box
                    sx={{
                        ...imageRowSx,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography sx={{ ...captionSx, m: 0 }}>No image</Typography>
                </Box>
            )}

            {progressSimple ? (
                <WirePlayProgressInfo
                    modeLine={modeLine}
                    onResume={handlePlay}
                    playing={playing}
                />
            ) : hidePlayButton ? (
                <WirePlaySummary gridSize={gridSize} difficulty={difficulty} />
            ) : (
                <WirePlayControls
                    templates={templates}
                    gridSize={gridSize}
                    difficulty={difficulty}
                    onGridChange={handleGridChange}
                    onDifficultyChange={handleDifficultyChange}
                    onPlay={handlePlay}
                    playing={playing}
                />
            )}
        </Box>
    );
}
